// frontend/src/components/LiveTesting.js version 1 works
import React, { useState, useEffect, useRef, useContext  } from 'react';
import { useTranslation } from 'react-i18next';
import { motion } from 'framer-motion';
import { 
  Mic,   AlertCircle, 
  MessageSquare, Headphones, Volume2, Phone, PhoneOff
} from 'lucide-react';

import { Container } from './ui/Container';
import io from 'socket.io-client';
import { v4 as uuidv4 } from 'uuid';
import KnowledgeBaseSection from './sections/KnowledgeBaseSection';
import PromptSettingsSection from './sections/PromptSettingsSection';
import { 
  getCustomGreeting, 
  getActiveKnowledgeBase, 
  getActivePromptTemplate, 
  bookAppointment, 
  processTranscript,
  getSystemConfiguration,
} from '../api';
import { AuthContext } from '../contexts/AuthContext';
import RateLimitAlert from './ui/RateLimitAlert';


const SOCKET_IO_URL = process.env.REACT_APP_SOCKET_IO_URL || 'http://localhost:5000';
const BASE_URL = process.env.REACT_APP_BASE_URL || 'http://localhost:5000'; 

const LiveTesting = () => {
  const { t } = useTranslation();
  const [isRecording, setIsRecording] = useState(false);
  const [transcription, setTranscription] = useState('');
  const [response, setResponse] = useState('');
  const [error, setError] = useState(null);
  const [conversationItems, setConversationItems] = useState([]);
  const [callId, setCallId] = useState(null);
  const [isProcessing, setIsProcessing] = useState(false);
  const socketRef = useRef(null);
  const audioContextRef = useRef(null);
  const scriptProcessorRef = useRef(null);
  const pcmDataRef = useRef([]);
  const audioSourceRef = useRef(null);
  const [conversationEnded, setConversationEnded] = useState(false);
  const [activeKnowledgeBase, setActiveKnowledgeBase] = useState(null);
  const [kbError, setKbError] = useState(null);
  const [activePrompt, setActivePrompt] = useState(null);
  const [promptError, setPromptError] = useState(null);
  const [isSocketConnected, setIsSocketConnected] = useState(false);
  const [isSessionCreated, setIsSessionCreated] = useState(false);
  const { user } = useContext(AuthContext);
  const [systemConfiguration, setSystemConfiguration] = useState(null);
  const [systemConfigError, setSystemConfigError] = useState(null);
  const [selectedVoice, setSelectedVoice] = useState('alloy');

  // New state to track when KB or prompt activations change
  const [kbActivationTimestamp, setKbActivationTimestamp] = useState(Date.now());
  const [promptActivationTimestamp, setPromptActivationTimestamp] = useState(Date.now());

  const [rateLimitError, setRateLimitError] = useState(false);
  const [rateLimitTimer, setRateLimitTimer] = useState(60000); // 60 seconds
  const rateLimitTimerRef = useRef(null);
  
  const [rateLimits, setRateLimits] = useState({
    requests: { limit: 0, remaining: 0, reset_seconds: 0 },
    tokens: { limit: 0, remaining: 0, reset_seconds: 0 }
  });

  const [loading, setLoading] = useState(false);

  const sessionStateRef = useRef(null);
  const noiseMetricsRef = useRef(null);
  const speechStateRef = useRef(null);
  const responseTimingRef = useRef(null);
  const interruptionRef = useRef(null);
  const speakingPaceRef = useRef(null);

  const [isContextLoaded, setIsContextLoaded] = useState(false);
  const [isPlaybackInterrupted, setIsPlaybackInterrupted] = useState(false);

  const onLoading = (isLoading) => {
    setLoading(isLoading);
  };

  /** COST CALCULATION */
  const [totalCost, setTotalCost] = useState(0);
  const [costDetails, setCostDetails] = useState({
    textInput: 0,
    cachedTextInput: 0,
    audioInput: 0,
    cachedAudioInput: 0,
    textOutput: 0,
    audioOutput: 0
  });
  const PRICING = {
    text: {
      input: 0.20 / 1_000_000,          // $2.50 per 1M tokens
      cachedInput: 0.05 / 1_000_000,    // $1.25 per 1M cached tokens
      output: 0.80 / 1_000_000         // $10.00 per 1M tokens
    },
    audio: {
      input: 10.00 / 1_000_000,        // $100.00 per 1M tokens
      cachedInput: 0.30 / 1_000_000,    // $20.00 per 1M cached tokens
      output: 20.00 / 1_000_000        // $200.00 per 1M tokens
    }
  };
  const initialDetails = {
      textInput:        0,
      cachedTextInput:  0,
      audioInput:       0,
      cachedAudioInput: 0,
      textOutput:       0,
      audioOutput:      0
     };

  const calculateCost = (usage) => {
    let cost = 0;
    let details = { ...initialDetails };  
    // Text Input
    const textInputCost = (usage.input_token_details.text_tokens * PRICING.text.input);
    cost += textInputCost;
    details.textInput = textInputCost;
  
    // Cached Text Input
    const cachedTextInputCost = (usage.input_token_details.cached_tokens_details.text_tokens * PRICING.text.cachedInput);
    cost += cachedTextInputCost;
    details.cachedTextInput = cachedTextInputCost;
  
    // Audio Input
    const audioInputCost = (usage.input_token_details.audio_tokens * PRICING.audio.input);
    cost += audioInputCost;
    details.audioInput = audioInputCost;
  
    // Cached Audio Input
    const cachedAudioInputCost = (usage.input_token_details.cached_tokens_details.audio_tokens * PRICING.audio.cachedInput);
    cost += cachedAudioInputCost;
    details.cachedAudioInput = cachedAudioInputCost;
  
    // Text Output
    const textOutputCost = (usage.output_token_details.text_tokens * PRICING.text.output);
    cost += textOutputCost;
    details.textOutput = textOutputCost;
  
    // Audio Output
    const audioOutputCost = (usage.output_token_details.audio_tokens * PRICING.audio.output);
    cost += audioOutputCost;
    details.audioOutput = audioOutputCost;
  
    return { cost, details };
  };
  
  /* END COST DECLARATIONS *///
  const onMessage = (message) => {
  };

  const onError = (errorMessage) => {
    console.error('Error:', errorMessage);
    setError(errorMessage);
  };

  // Modified handler for KB activation
  const handleKbActivation = () => {
    setKbActivationTimestamp(Date.now());
  };

  // Modified handler for prompt activation
  const handlePromptActivation = () => {
    setPromptActivationTimestamp(Date.now());
  };

  // First useEffect: Initial fetch of active KB, Prompt, and system configuration in parallel
  // optimizes the loading time and also ensures that all necessary contexts are fetched before proceeding.
  useEffect(() => {
    const initializeState = async () => {
      try {
        await Promise.all([
          fetchActiveKnowledgeBase(),
          fetchActivePrompt(),
          fetchSystemConfiguration()
        ]);
      } catch (error) {
        console.error('Initialization error:', error);
        // Optionally, set an error state here if needed
        setError('Failed to initialize application. Please try again.');
      }
    };
    initializeState();
  }, []);

  // New useEffect: Set isContextLoaded when both activePrompt and activeKnowledgeBase are loaded
  useEffect(() => {
    if (activePrompt && activeKnowledgeBase) {
      setIsContextLoaded(true);
    }
  }, [activePrompt, activeKnowledgeBase]);

  // New useEffect: Re-fetch KB when activation changes
  useEffect(() => {
    if (kbActivationTimestamp > 0) {
      fetchActiveKnowledgeBase();
    }
  }, [kbActivationTimestamp]);

  // New useEffect: Re-fetch prompt when activation changes
  useEffect(() => {
    if (promptActivationTimestamp > 0) {
      fetchActivePrompt();
    }
  }, [promptActivationTimestamp]);

  // Second useEffect: Initialize Socket.io connection once on mount
  useEffect(() => {

    // Initialize Socket.io connection to the backend    
    
      socketRef.current = io(SOCKET_IO_URL, {
        transports: ['websocket'], // Specify WebSocket transport only
        reconnectionAttempts: 5,    // Optional: Limit reconnection attempts
        timeout: 10000,             // Optional: Set connection timeout (in ms)
      });

      if (socketRef.current) {
        socketRef.current.on('connect', () => 
          {
          setIsSocketConnected(true);

          // Emit 'authenticate' event with the token
          const token = localStorage.getItem('jwtToken');

          if (token) {
            socketRef.current.emit('authenticate', { token });

          } else {
            console.error('No JWT token found in localStorage.');
            setError('Authentication failed. Please log in again.');
          }
        });

      // Handle unauthorized event from the server
      socketRef.current.on('unauthorized', (error) => 
        {
        console.error('Socket unauthorized:', error.message);
        setError('Authentication failed. Please log in again.');
        // Optionally, redirect to login page or logout the user
      });

      socketRef.current.on('disconnect', () => 
        {
        setIsSocketConnected(false);
      });
    }
      // Listen for 'new-call' events from the backend to get the Call ID
      socketRef.current.on('new-call', (newCall) => {
        setCallId(newCall._id); // Store the Call ID
            setIsSocketConnected(true);
      });

      // Listen for 'openai-message' events from the backend
      socketRef.current.on('openai-message', (data) => {
        switch (data.type) {
          case 'session.created':
            handleSessionCreated(data);
            break;
          case 'conversation.item.created':
            handleConversationItemCreated(data);
            break;        
          case 'response.audio.delta':
            handleAudioDelta(data);
            break;
          case 'response.text.delta':
            handleTextDelta(data);
            break;
          case 'input_audio_buffer.speech_started':
            handleSpeechStartedEvent();
            break;
          case 'input_audio_buffer.speech_stopped':
            //introduced the issue of buffer to small commit
            //handleSpeechStoppedEvent(data);
            break;
          case 'response.audio.done':
            handleAudioDone(data);
            break;
          case 'response.audio_transcript.delta':
            handleAudioTranscriptDelta(data);
            break;
          case 'response.audio_transcript.done': //output transcription
            handleAudioTranscriptDone(data);
            break;
          case 'response.done':  
            handleResponseDone(data);
            break;
          case 'response.output_item.added':
             //cette fonction introduit des régressions
            //handleResponseOutputItemAdded(data);
            break;
          case 'conversation.item.input_audio_transcription.completed': //input transcription
            handleTranscriptionCompleted(data);
            break;
          case 'conversation.finished':
            handleConversationFinished(data);
            break;
          case 'rate_limits.updated':
            handleRateLimitsUpdated(data);
            break;   
          case 'conversation.item.truncated':
            //cette fonction introduit des régressions
            //handleConversationItemTruncated(data);
            break; 
          case 'error':
            handleError(data);
            break;            
          default:
            break;
        }
      });

      // Listen for 'call-update' events
      socketRef.current.on('call-update', (update) => {
        // You can update state or UI based on the update
        // For example, display calendar event link
        if (update.calendarEventId) {
          // Optionally, fetch the event details or provide a link
          const eventLink = `https://calendar.google.com/calendar/event?eid=${update.calendarEventId}`;
          alert(t('LiveTesting.alerts.meetingBooked', { eventLink })); // Ensure this key exists in your translation file
        }
      });
        
      // Listen for status updates from the backend
      socketRef.current.on('openai-status', (status) => {
      });

      // Listen for errors from the backend
      socketRef.current.on('openai-error', (error) => {
        console.error('OpenAI WebSocket Error:', error);
        setError(error.message || t('LiveTesting.alerts.openAIError')); // Ensure this key exists
      });
    
    // Cleanup on component unmount
    return () => {
      if (socketRef.current) {
        socketRef.current.disconnect();
      }
      if (audioContextRef.current) {
        audioContextRef.current.close();
      }
    };
  }, []); // Empty dependency array ensures this runs once

  // Third useEffect: Handle actions based on activeKnowledgeBase and activePrompt
  useEffect(() => {
    const shouldUpdateSession = 
      activeKnowledgeBase &&
      activePrompt &&
      !kbError &&
      !promptError &&
      socketRef.current?.connected &&
      selectedVoice;

    if (shouldUpdateSession) {
      const knowledgeBaseText = activeKnowledgeBase.text || '';
      const promptContent = activePrompt.content || '';
      const instructions = promptContent + '\n' + knowledgeBaseText;

      const sessionUpdateEvent = {
        type: 'session.update',
        session: {
          modalities: ['text', 'audio'],
          instructions: instructions,
          input_audio_transcription: {model: 'whisper-1'},
          turn_detection: {
            type: 'server_vad',
            threshold: activePrompt.threshold,             // Use prompt's threshold
            prefix_padding_ms: activePrompt.prefixPadding, // Use prompt's prefix padding
            silence_duration_ms: activePrompt.silenceDuration, // Use prompt's silence duration
          },
          voice: selectedVoice, 
        },
      };

      socketRef.current.emit('client-event', sessionUpdateEvent);
    }
  }, [activeKnowledgeBase, activePrompt, kbError, promptError, selectedVoice, socketRef.current?.connected]);

    // Fourth useEffect: Handle session creation
    useEffect(() => {
      if (isSessionCreated && activeKnowledgeBase && activePrompt && !kbError && !promptError && selectedVoice) {
        sendSessionUpdate();
      }
    }, [isSessionCreated, activeKnowledgeBase, activePrompt, kbError, promptError, selectedVoice]);

  // Add cleanup for the timer in useEffect
  useEffect(() => {
    return () => {
      if (rateLimitTimerRef.current) {
        clearInterval(rateLimitTimerRef.current);
      }
    };
  }, []);

  // Add the retry handler
  const handleRetry = () => {
    if (rateLimitTimer <= 0 && rateLimits.requests.remaining > 0) {
      setRateLimitError(false);
      setError(null);
      setIsSocketConnected(true);
    }
  };

  // **Updated Fetch Functions Using Centralized API Functions**
  
  const fetchSystemConfiguration = () => {
    getSystemConfiguration()
      .then((config) => {
        setSystemConfiguration(config);
        setSystemConfigError(null);
  
        if (config.voice && config.voice.selectedVoice) {
          setSelectedVoice(config.voice.selectedVoice);
        } else {
          console.log('No user-configured voice found. Using default voice.');
        }
      })
      .catch((error) => {
        console.error('Error fetching system configuration:', error);
        setSystemConfigError(error.message || 'Failed to fetch system configuration.');
      });
  };
  
  const fetchActiveKnowledgeBase = () => {
    
    getActiveKnowledgeBase()
      .then((knowledgeBase) => {
        //console.log('Active Knowledge Base Response:', knowledgeBase);
  
        if (knowledgeBase && !knowledgeBase.error) {
          // Active KB found via /knowledge-base/active
          setActiveKnowledgeBase(knowledgeBase);
          setKbError(null); // Clear any previous KB errors
          //console.log('200 Active Knowledge Base Set LIVETESTING:', knowledgeBase);
        } else {
          // Handle 204 No Content or other scenarios where active KB isn't found
          setKbError(knowledgeBase.error || t('LiveTesting.alerts.noActiveKnowledgeBaseSet'));

        }
      })
      .catch((error) => {
               if (error.response && error.response.status === 404) {
          setKbError(t('LiveTesting.alerts.noActiveKnowledgeBaseSet')); // Ensure this key exists
        } else {
          setKbError(t('LiveTesting.alerts.fetchKbError')); // Ensure this key exists
        }
        
      })
  };
  
  const fetchActivePrompt = () => {

  
    getActivePromptTemplate()
      .then((prompt) => {

        if (prompt && !prompt.error) {
          setActivePrompt(prompt);
          //console.log('3- Time Active Prompt Set:', prompt);
        } else {
          setPromptError(prompt.error || t('LiveTesting.alerts.noActivePrompt')); // Ensure this key exists
          console.warn('4- Time Active Prompt Error:', prompt.error);
        }
      })
      .catch((error) => {
        if (error.response && error.response.status === 404) {
          console.warn('5- Time No active prompt set.');
          setPromptError(t('LiveTesting.alerts.noActivePrompt')); // Ensure this key exists
        } else {
          console.error('6- time  Error fetching prompt:', error);
          setPromptError(t('LiveTesting.alerts.fetchPromptError')); // Ensure this key exists
        }
      });
      //console.log('7- END Fetching Active Prompt time :', new Date().toISOString() + " ---" +  activePrompt);

  };
  
  // STEP 1: Handle session.created here we set setIsSessionCreated to true
  const handleSessionCreated = (data) => {
    setIsSessionCreated(true);
  };

  // STEP 2: Emit client-event and send session update with instructions to backend / openai socket
  const sendSessionUpdate = () => {
    if (!activePrompt) {
      console.error('time No active prompt to send :' + activePrompt);
      setError(t('LiveTesting.alerts.noActivePromptSet'));
      return;
    }
    if (!activeKnowledgeBase) {
      console.error('No active knowledge base to send.');
      setError(t('LiveTesting.alerts.noActiveKnowledgeBaseSet'));
      return;
    }
    if (!selectedVoice) {
      console.error('No selected voice found.');
      setError('No selected voice found.');
      return;
    }    
    const knowledgeBaseText = activeKnowledgeBase.text || '';
    const promptContent = activePrompt.content || '';
    const instructions = promptContent + '\n' + knowledgeBaseText;
    const sessionUpdateEvent = {
      type: 'session.update',
      session: {
          modalities: ['text', 'audio'],
          instructions: instructions,
          input_audio_transcription: {model: 'whisper-1'},
          turn_detection: {
              type: 'server_vad',
              threshold: activePrompt.threshold,             // Use prompt's threshold
              prefix_padding_ms: activePrompt.prefixPadding, // Use prompt's prefix padding
              silence_duration_ms: activePrompt.silenceDuration, // Use prompt's silence duration
          },
          voice: selectedVoice,
      },
    };

    socketRef.current.emit('client-event', sessionUpdateEvent);
  };

  // Handle assistant's text response REFACTOR DEAD CODE
  const handleConversationItemCreated = (data) => {
    const newItem = data.item;
    setConversationItems((prevItems) => [...prevItems, newItem]); // Update conversation items
    
    if (newItem.type === 'message' && newItem.role === 'assistant') {
      newItem.content.forEach((contentItem) => {
        if (contentItem.type === 'input_text') {
          setResponse((prev) => prev + contentItem.text + ' ');
  
          // Check if the assistant confirms an appointment
          if (contentItem.text.includes('Rendez-vous pris')) {
            const appointmentDetails = {
              user: 'Client Name',
              date: 'Date',
              time: 'Time',
              callId,
            };
  
            bookAppointment(appointmentDetails)
              .then((response) => {
              })
              .catch((error) => {
                console.error('Error booking appointment:', error);
              });
          }
        }
      });
  
      // Trigger response generation with audio
      if (socketRef.current?.connected) {
        const responseEvent = {
          type: 'response.create',
          response: {
            modalities: ['text', 'audio'],
            voice: selectedVoice
          }
        };
        socketRef.current.emit('client-event', responseEvent);
      }
    }
  };

  // STEP 3:Handle incoming audio, accumulate the audio chunks for later playback
  const handleAudioDelta = (data) => {
    // data.delta contains Base64-encoded PCM16 audio data at 24kHz
    const audioData = data.delta;
    const decodedAudio = base64ToUint8Array(audioData);
    // Accumulate PCM data as Uint8Arrays
    pcmDataRef.current.push(decodedAudio);
  };

  // STEP 5:Handle completion of audio streaming, playback to browser
  const handleAudioDone = (data) => {  
    if (isPlaybackInterrupted) {
      console.warn('Waiting for truncation acknowledgment before starting new playback.');
      return; // Wait until the server confirms truncation
    }

    // If no PCM data is available, log and skip playback
    if (pcmDataRef.current.length === 0) {
      console.warn('No audio data received from the server.');
      pcmDataRef.current = []; // Reset PCM data
      setIsProcessing(false);
      return;
    }
  
    try {
      // Concatenate all Uint8Arrays
      const totalLength = pcmDataRef.current.reduce((sum, arr) => sum + arr.length, 0);
      const concatenated = new Uint8Array(totalLength);
      let offset = 0;
      pcmDataRef.current.forEach((arr) => {
        concatenated.set(arr, offset);
        offset += arr.length;
      });
      pcmDataRef.current = []; // Reset PCM data
  
      // Ensure buffer is properly aligned
      const buffer = concatenated.buffer.slice(
        concatenated.byteOffset,
        concatenated.byteOffset + concatenated.byteLength
      );
  
      // Create an Int16Array from the buffer
      const int16Array = new Int16Array(buffer);
  
      // Convert Int16Array to Float32Array normalized between -1 and 1
      const float32Array = new Float32Array(int16Array.length);
      for (let i = 0; i < int16Array.length; i++) {
        float32Array[i] = int16Array[i] / 32768;
      }
  
      // Initialize AudioContext if not already
      if (!audioContextRef.current) {
        audioContextRef.current = new (window.AudioContext || window.webkitAudioContext)();
      }
  
      // Ensure no existing playback is ongoing
      if (audioSourceRef.current) {
        audioSourceRef.current.stop();
        audioSourceRef.current.disconnect();
        audioSourceRef.current = null;
      }
  
      // Create AudioBuffer
      const audioBuffer = audioContextRef.current.createBuffer(
        1, // mono channel
        float32Array.length,
        24000 // sample rate (24kHz)
      );
      audioBuffer.copyToChannel(float32Array, 0);
  
      // Play the AudioBuffer
      playAudioBuffer(audioBuffer);
    } catch (error) {
      console.error('Error during audio playback:', error);
      setError('Audio playback failed');
    }
  };
  
  // Handle interruption when the caller's speech starts
    const handleSpeechStartedEvent = () => {
      // Only proceed if there's actually audio playing
      if (audioSourceRef.current) {
        try {
          // 1.1 Stop current audio playback
          audioSourceRef.current.stop();
          audioSourceRef.current.disconnect();
          audioSourceRef.current = null;
    
          //1.2 Set playback interrupted state
          setIsPlaybackInterrupted(true);

          // 2. Clear buffered PCM data
          pcmDataRef.current = [];
    
          // 3. Send truncation event to OpenAI
          const truncateEvent = {
            type: 'conversation.item.truncate',
            content_index: 0,
            audio_end_ms: 10
          };
    
          if (socketRef.current?.connected) {
            socketRef.current.emit('client-event', truncateEvent);
          }
    
          // 4. Update UI state
          setResponse(prev => prev + ' [Interrupted]');
          setIsProcessing(false);
    
        } catch (error) {
          console.error('Error during interruption handling:', error);
          setError('Error handling interruption');
        }
      }
    };
  
  // REFACTOR THIS IS NEVER RECEIVED setConversationEnded to true
  const handleConversationFinished = (data) => {
    setConversationEnded(true);
    // After the conversation has ended, send the transcript for processing
    processTranscriptLT();
  };

  // STEP 4:Handle incoming transcription deltas accumulate the transcript chunks for later processing
  // Changed to Include Speaking Speed Analysis and Topic Extraction:
  const handleAudioTranscriptDelta = (data) => {
    if (data.transcript) {
      setTranscription((prev) => {
        const updated = prev + data.transcript + ' ';
        return updated;
      });
    }
  };

   // STEP 6:Handle completed transcription input transcription
   const handleTranscriptionCompleted = (data) => {
    setTranscription((prev) => {
      const updated = prev + data.transcript + ' ';
      return updated;
    });
  };

  // STEP 7:Handle completion of transcription output transcript
  const handleAudioTranscriptDone = (data) => {
    if (data.transcript) {
      setTranscription((prev) => {
        const updated = prev + data.transcript + ' ';
        return updated;
      });
    }
  };

  // Add this new handler function after your other handler functions
  /** important changes
   * removed Stop recording if it's still ongoing
   * added session udpate
   */
  const handleResponseDone = (data) => {
  if (data.response.status === 'failed') {
    const { error } = data.response.status_details;
    if (error?.code === 'rate_limit_exceeded') {
      handleRateLimitExceeded(error.reset_seconds || 60);
      return;
    }
    setError(`Response failed: ${error?.message || 'Unknown error'}`);
    return;
  }
    // Extract usage information
    const usage = data.response.usage;
    if (usage) {
      const { cost, details } = calculateCost(usage);
      setTotalCost(cost);
      setCostDetails(details);

    }
  
    // **Add this block to set the assistant's response**
    if (data.response.output && data.response.output.length > 0) {
      const assistantResponse = data.response.output
        .map(item =>
          item.content.map(content => content.transcript || '').join(' ')
        )
        .join('\n');
      setResponse(assistantResponse);
    } else {
      console.warn('No valid output found in response.');
    }
};

  const handleRateLimitsUpdated = (data) => {
    
    // Debugging: Log the entire rate_limits array
    
    const requestsLimit = data.rate_limits.find(limit => limit.name === 'requests');
    const tokensLimit = data.rate_limits.find(limit => limit.name === 'tokens');
        
    if (requestsLimit && tokensLimit) {
      setRateLimits({
        requests: {
          limit: requestsLimit.limit,
          remaining: requestsLimit.remaining,
          reset_seconds: requestsLimit.reset_seconds,
        },
        tokens: {
          limit: tokensLimit.limit,
          remaining: tokensLimit.remaining,
          reset_seconds: tokensLimit.reset_seconds,
        }
      });
  
      // Determine if rate limit has been exceeded
      if (requestsLimit.remaining === 0 || tokensLimit.remaining === 0) {
        setRateLimitError(true);
        setRateLimitTimer(Math.ceil(Math.min(requestsLimit.reset_seconds, tokensLimit.reset_seconds)) * 1000);
        
        // Start the countdown timer based on the minimum reset_seconds
        if (rateLimitTimerRef.current) {
          clearInterval(rateLimitTimerRef.current);
        }
        
        rateLimitTimerRef.current = setInterval(() => {
          setRateLimitTimer(prev => {
            if (prev <= 1000) {
              clearInterval(rateLimitTimerRef.current);
              setRateLimitError(false);
              return 0;
            }
            return prev - 1000;
          });
        }, 1000);
      }
    } else {
      console.warn('One or both rate limits ("requests", "tokens") not found in the event data.');
    }
  };

  // Add this function to handle when rate limit is exceeded
  const handleRateLimitExceeded = (resetSeconds) => {
    // Stop recording if it's still ongoing
    if (isRecording) {
      stopRecording();
    }
    
    // Clear any ongoing audio playback
    if (audioSourceRef.current) {
      audioSourceRef.current.stop();
      audioSourceRef.current.disconnect();
      audioSourceRef.current = null;
    }
    
    // Reset states
    setIsProcessing(false);
    pcmDataRef.current = [];
    
    // Update rate limit UI state
    setRateLimitError(true);
    setRateLimitTimer(resetSeconds * 1000);
    
    // Start countdown timer
    if (rateLimitTimerRef.current) {
      clearInterval(rateLimitTimerRef.current);
    }
    
    rateLimitTimerRef.current = setInterval(() => {
      setRateLimitTimer((prev) => {
        if (prev <= 1000) {
          clearInterval(rateLimitTimerRef.current);
          setRateLimitError(false);
          return 0;
        }
        return prev - 1000;
      });
    }, 1000);
  };

  const handleTextDelta = (data) => {
    if (!data.delta) return;
    
    // Monitor for potential interruptions
    const lastUserEventTime = speechStateRef.current?.startTime || 0;
    const timeSinceUserEvent = Date.now() - lastUserEventTime;
    
    if (timeSinceUserEvent < 500) {
      socketRef.current?.emit('client-event', {
        type: 'response.cancel'
      });
    }
  };

  const handleConversationItemTruncated = (data) => {
  
    // Reset interruption state
    setIsPlaybackInterrupted(false);
  
    // Clear buffers
    pcmDataRef.current = [];
  };

  const handleResponseOutputItemAdded = (data) => {
    const speakingPace = speakingPaceRef.current?.wps || 2;
    const interruptionCount = interruptionRef.current?.count || 0;
  
    // Adjust voice parameters based on user profile
    if (socketRef.current?.connected) {
      socketRef.current.emit('client-event', {
        type: 'session.update',
        session: {
          temperature: interruptionCount > 3 ? 0.6 : 0.8,
          instructions: `${activePrompt?.content || ''}\n${activeKnowledgeBase?.text || ''}\n` +
            `Speak ${speakingPace > 3 ? 'quickly' : speakingPace < 1 ? 'slowly' : 'at a moderate pace'}. ` +
            `${interruptionCount > 3 ? 'Keep responses brief and direct.' : ''}`
        }
      });
    }
  };
  
  //new version to manage if server reports frequent errors related to audio
  const handleError = (data) => {
    const { error } = data;
    console.error('Error:', error);
  
    if (error.type === 'audio_error' || error.type === 'transcription_error') {
      noiseMetricsRef.current = {
        count: (noiseMetricsRef.current?.count || 0) + 1,
        lastError: Date.now(),
      };
  
      if (noiseMetricsRef.current.count > 2 && socketRef.current?.connected) {
        const updatedThreshold = Math.min(0.8, 0.5 + noiseMetricsRef.current.count * 0.1);
  
        socketRef.current.emit('client-event', {
          type: 'session.update',
          session: { turn_detection: { threshold: updatedThreshold } },
        });
      }
    }
  
    setError(error.message || 'An error occurred');
  };
  
  
  const handleSpeechStoppedEvent = (data) => {
  
    const speechDuration = Date.now() - (speechStateRef.current?.startTime || Date.now());
    const averageAmplitude = speechStateRef.current?.amplitudeSum / speechStateRef.current?.sampleCount || 0.5;
  
    // Determine new turn detection settings
    const newTurnDetectionConfig = {
      type: 'server_vad',
      threshold: averageAmplitude > 0.7 ? 0.7 : averageAmplitude > 0.3 ? 0.5 : 0.3,
      prefix_padding_ms: averageAmplitude < 0.3 ? 500 : 300,
      silence_duration_ms: speechDuration < 1000 ? 300 : 500,
    };
  
  
    if (socketRef.current?.connected) {
      socketRef.current.emit('client-event', {
        type: 'session.update',
        session: { turn_detection: newTurnDetectionConfig },
      });
    }
  
    // Update session with the latest context
    updateSessionWithCurrentContext();
  
    // Reset speech state
    speechStateRef.current = null;
  };
  
   
  const updateSessionWithCurrentContext = () => {
    if (!socketRef.current?.connected) {
      console.warn('Socket not connected');
      return;
    }  
    // Fetch fresh values if needed
    if (!activePrompt || !activeKnowledgeBase) {
      fetchActivePrompt();
      fetchActiveKnowledgeBase();
      return;
    }  
    const instructions = activePrompt.content  + '\n' + activeKnowledgeBase.text;
    const sessionUpdate = {
      type: 'session.update',
      session: {
        modalities: ['text', 'audio'],
        instructions: instructions,
        input_audio_transcription: { model: 'whisper-1' },
        turn_detection: {
          type: 'server_vad',
          threshold: activePrompt.threshold,
          prefix_padding_ms: activePrompt.prefixPadding,
          silence_duration_ms: activePrompt.silenceDuration
        },
        voice: selectedVoice
      }
    };
  
    socketRef.current.emit('client-event', sessionUpdate);
  };

  const analyzeTopics = (sentences) => {
    // Simple topic analysis - could be enhanced with NLP
    const topics = new Set();
    const commonTopicWords = ['appointment', 'schedule', 'booking', 'information', 'help', 'question'];
    
    sentences.forEach(sentence => {
      commonTopicWords.forEach(topic => {
        if (sentence.toLowerCase().includes(topic)) {
          topics.add(topic);
        }
      });
    });
    
    return Array.from(topics);
  };  

  // Start recording audio
  const startRecording = async () => {
    // Check socket connection
    if (!isSocketConnected) {
      console.error('Socket not connected.');
      setError('We are connecting you. Please wait...');
      return;
    }
    
    if (!socketRef.current || socketRef.current.disconnected) {
      console.error('Socket not initialized.');
      setError('Connection failed. Please try again.');
      return;
    }
  
    try {
      // Stop any existing audio playback
      if (audioSourceRef.current) {
        audioSourceRef.current.stop();
        audioSourceRef.current.disconnect();
        audioSourceRef.current = null;
      }
  
      // Clear previous states
      setTranscription('');
      setResponse('');
      setError(null);
      setConversationItems([]); 
      setConversationEnded(false);
      pcmDataRef.current = [];
  
      // Create new call log
      const conversationId = uuidv4();
      const callDetails = {
        conversationId,
        callerName: 'Unknown',
        phoneNumber: user?.email || 'Web user interface',
      };
      socketRef.current.emit('log-call', callDetails);
  
      // Play greeting before starting recording
      try {
        const greetingResponse = await getCustomGreeting();

        if (greetingResponse?.audioUrl) {
        console.log('URL: ' + greetingResponse.audioUrl);

          const fullAudioUrl = greetingResponse?.audioUrl.startsWith('/') 
                              ? `${BASE_URL}${greetingResponse.audioUrl}`
                              : `${BASE_URL}/${greetingResponse.audioUrl}`; 
 


          console.log('fullAudioUrl: ' +fullAudioUrl);
          console.log('BASE_URL: ' +BASE_URL);

          const audio = new Audio(fullAudioUrl);
          await audio.play();
          // Wait for greeting to finish
          await new Promise((resolve, reject) => {
            audio.onended = resolve;
            audio.onerror = reject;
          });
        }
      } catch (greetingError) {
        console.error('Error playing greeting:', greetingError);
        // Continue with recording even if greeting fails
      }
  
      // Start recording after greeting (or if greeting failed)
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      audioContextRef.current = new (window.AudioContext || window.webkitAudioContext)();
      const source = audioContextRef.current.createMediaStreamSource(stream);
  
      // Create a ScriptProcessorNode for processing audio
      scriptProcessorRef.current = audioContextRef.current.createScriptProcessor(4096, 1, 1);
      source.connect(scriptProcessorRef.current);
      scriptProcessorRef.current.connect(audioContextRef.current.destination);
  
      scriptProcessorRef.current.onaudioprocess = (audioProcessingEvent) => {
        const inputBuffer = audioProcessingEvent.inputBuffer.getChannelData(0);
        
        // Calculate amplitude
        const amplitude = Math.sqrt(inputBuffer.reduce((sum, sample) => sum + sample * sample, 0) / inputBuffer.length);
      
        // Initialize speech state tracking if it doesn't exist
        if (!speechStateRef.current) {
          speechStateRef.current = { startTime: Date.now(), amplitudeSum: 0, sampleCount: 0 };
        }
      
        // Update speech state
        speechStateRef.current.amplitudeSum += amplitude;
        speechStateRef.current.sampleCount += 1;
      
        // Continue with existing audio processing
        // Clone the buffer
        const buffer = new Float32Array(inputBuffer);
        // Process the audio buffer
        processAudio(buffer);
      };
      
  
      setIsRecording(true);
  
    } catch (err) {
      console.error('Error accessing microphone:', err);
      setIsRecording(false);
      setError(t('LiveTesting.alerts.microphoneError'));
    }
  };

  // Stop recording audio
  const stopRecording = () => {

    processTranscriptLT();

    setIsRecording(false);
    if (scriptProcessorRef.current) {
      scriptProcessorRef.current.disconnect();
      scriptProcessorRef.current.onaudioprocess = null;
    }
    if (audioContextRef.current) {
      audioContextRef.current.close();
      audioContextRef.current = null;
    }

    // Stop any existing audio playback
    if (audioSourceRef.current) {
      audioSourceRef.current.stop();
      audioSourceRef.current.disconnect();
      audioSourceRef.current = null;
    }
     // Commit the audio buffer manually
     if (pcmDataRef.current.length > 0) {
      // Commit the audio buffer manually
      const commitEvent = {
        type: 'input_audio_buffer.commit',
        // 'event_id' is optional; the server will assign one if not provided
      };
      socketRef.current.emit('client-event', commitEvent);
    } else {
      // Optionally, emit a different event to notify the backend
      // socketRef.current.emit('client-event', { type: 'call.stopped' });
    }
  };
  
  // Process the transcript after conversation ends
  const processTranscriptLT = () => {


    if (!callId || !transcription) {
      console.error('Call ID or transcription is missing.');
      return;
    }
    const tasks = ['transcript','extractName', 'bookMeeting','extractPhoneNumber', 'summary', 'actionItems', 'keywords','extractStatus'];

    //const tasks = ['extractName', 'bookMeeting','extractPhoneNumber', 'summary', 'actionItems', 'keywords'];

    tasks.forEach((task) => {
      processTranscript(callId, transcription, task)
        .then((response) => {
        })
        .catch((error) => {
          console.error(`Error processing task ${task}:`, error);
        });
    });
  };

  // Process and send audio data
  const processAudio = async (buffer) => {
    try {
      // Resample the audio buffer to 24kHz
      const resampledBuffer = await resampleAudio(
        buffer,
        audioContextRef.current.sampleRate,
        24000
      );

      // Convert Float32Array to PCM16
      const pcm16Buffer = floatTo16BitPCM(resampledBuffer);
      // Base64 encode
      const base64Audio = arrayBufferToBase64(pcm16Buffer);

      // Create the audio event as per OpenAI's Realtime API
      const event = {
        type: 'input_audio_buffer.append',
        audio: base64Audio,
        // 'event_id' is optional; the server will assign one if not provided
      };

      // Send to backend via Socket.io
      socketRef.current.emit('client-event', event);
    } catch (err) {
      console.error('Error processing audio:', err);
      setError(t('LiveTesting.alerts.audioProcessingError')); // Ensure this key exists
    }
  };

  // Helper functions...

  const resampleAudio = (buffer, originalSampleRate, targetSampleRate) => {
    return new Promise((resolve, reject) => {
      const offlineCtx = new OfflineAudioContext(
        1,
        buffer.length * targetSampleRate / originalSampleRate,
        targetSampleRate
      );
      const audioBuffer = offlineCtx.createBuffer(1, buffer.length, originalSampleRate);
      audioBuffer.copyToChannel(buffer, 0);
      const source = offlineCtx.createBufferSource();
      source.buffer = audioBuffer;
      source.connect(offlineCtx.destination);
      source.start(0);
      offlineCtx
        .startRendering()
        .then((renderedBuffer) => {
          const resampledData = renderedBuffer.getChannelData(0);
          resolve(resampledData);
        })
        .catch((err) => {
          console.error('Error during resampling:', err);
          reject(err);
        });
    });
  };

  const floatTo16BitPCM = (input) => {
    const buffer = new Int16Array(input.length);
    for (let i = 0; i < input.length; i++) {
      let s = Math.max(-1, Math.min(1, input[i]));
      buffer[i] = s < 0 ? s * 0x8000 : s * 0x7FFF;
    }
    return buffer.buffer;
  };

  const arrayBufferToBase64 = (buffer) => {
    let binary = '';
    const bytes = new Uint8Array(buffer);
    const len = bytes.byteLength;
    for (let i = 0; i < len; i++) {
      binary += String.fromCharCode(bytes[i]);
    }
    return window.btoa(binary);
  };

  const base64ToUint8Array = (base64) => {
    const binary_string = window.atob(base64);
    const len = binary_string.length;
    const bytes = new Uint8Array(len);
    for (let i = 0; i < len; i++) {
      bytes[i] = binary_string.charCodeAt(i);
    }
    return bytes;
  };

  const playAudioBuffer = (audioBuffer) => {
    const source = audioContextRef.current.createBufferSource();
    source.buffer = audioBuffer;
    source.connect(audioContextRef.current.destination);
    source.start(0);
    audioSourceRef.current = source;

    // Listen for when the audio playback ends
    source.onended = () => {
      audioSourceRef.current = null;
    };
  };

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Page Header */}
      <div className="bg-white border-b border-gray-200">
        <Container className="py-6 px-4 sm:px-6 lg:px-20">
          <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between">
            <div className="mb-4 lg:mb-0">
              <h1 className="text-2xl font-bold text-gray-900 sm:text-3xl">
                {t('LiveTesting.title')}
              </h1>
              <p className="mt-2 text-sm text-gray-600">
                {t('LiveTesting.description')}
              </p>
            </div>

            <div className="flex gap-3">
              {/* Call Button */}
              <button
                onClick={isRecording ? stopRecording : startRecording}
                disabled={isProcessing || !isSocketConnected || !isContextLoaded}
                className={`
                  relative
                  flex
                  items-center
                  justify-center
                  gap-2
                  px-6
                  py-3
                  rounded-full
                  shadow-lg
                  transition-all
                  duration-200
                  ${isRecording 
                    ? 'bg-red-500 hover:bg-red-600 text-white animate-pulse' 
                    : 'bg-emerald-500 hover:bg-emerald-600 text-white'
                  }
                  ${(isProcessing || !isSocketConnected) ? 'opacity-50 cursor-not-allowed' : 'transform hover:scale-105'}
                `}
              >
                {isRecording ? (
                  <>
                    <PhoneOff className="w-5 h-5" />
                    <span className="font-semibold">{t('LiveTesting.buttons.stop_recording')}</span>
                    <span className="absolute -top-1 -right-1 flex h-3 w-3">
                      <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-red-400 opacity-75"></span>
                      <span className="relative inline-flex rounded-full h-3 w-3 bg-red-500"></span>
                    </span>
                  </>
                ) : (
                  <>
                    <Phone className="w-5 h-5" />
                    <span className="font-semibold">{t('LiveTesting.buttons.start_call')}</span>
                  </>
                )}
              </button>
            </div>
          </div>

          {/* Status Cards */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mt-6">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              className="bg-white p-6 rounded-xl border border-gray-100 shadow-sm"
            >
              <div className="flex items-center">
                <div className="p-2 bg-violet-50 rounded-lg">
                  <Mic className={`w-6 h-6 text-violet-600 ${isRecording ? 'animate-pulse' : ''}`} />
                </div>
                <div className="ml-4">
                  <p className="text-sm font-medium text-gray-600">{t('LiveTesting.status_cards.recording_status.title')}</p>
                  <p className="text-2xl font-semibold text-gray-900">
                    {isRecording ? t('LiveTesting.status_cards.recording_status.active') : t('LiveTesting.status_cards.recording_status.ready')}
                  </p>
                </div>
              </div>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.1 }}
              className="bg-white p-6 rounded-xl border border-gray-100 shadow-sm"
            >
              <div className="flex items-center">
                <div className="p-2 bg-emerald-50 rounded-lg">
                  <MessageSquare className="w-6 h-6 text-emerald-600" />
                </div>
                <div className="ml-4">
                  <p className="text-sm font-medium text-gray-600">{t('LiveTesting.status_cards.messages.title')}</p>
                  <p className="text-2xl font-semibold text-gray-900">
                    {conversationItems.length}
                  </p>
                </div>
              </div>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.2 }}
              className="bg-white p-6 rounded-xl border border-gray-100 shadow-sm"
            >
              <div className="flex items-center">
                <div className="p-2 bg-blue-50 rounded-lg">
                  <Headphones className="w-6 h-6 text-blue-600" />
                </div>
                <div className="ml-4">
                  <p className="text-sm font-medium text-gray-600">{t('LiveTesting.status_cards.audio_status.title')}</p>
                  <p className="text-2xl font-semibold text-gray-900">
                    {audioSourceRef.current ? t('LiveTesting.status_cards.audio_status.playing') : t('LiveTesting.status_cards.audio_status.ready')}
                  </p>
                </div>
              </div>
            </motion.div>
          </div>
        </Container>
      </div>

      {rateLimitError && (
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          exit={{ opacity: 0, y: -20 }}
          className="mb-6"
        >
          <RateLimitAlert
            remainingTime={rateLimitTimer}
            onRetry={handleRetry}
            rateLimits={rateLimits}
          />
        </motion.div>
      )}

      {/* Main Content */}
      <Container className="py-6 px-4 sm:px-6 lg:px-20">
        {error && (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="bg-red-50 border border-red-200 rounded-xl p-4 mb-6"
          >
            <div className="flex">
              <AlertCircle className="h-5 w-5 text-red-400" />
              <div className="ml-3">
                <h3 className="text-sm font-medium text-red-800">{t('LiveTesting.alerts.error.title')}</h3>
                <p className="mt-1 text-sm text-red-600">{error}</p>
              </div>
            </div>
          </motion.div>
        )}

        {/* All Cards in a single grid with one column */}
        <div className="grid grid-cols-1 gap-6">
          {/* Transcription Card */}
          <motion.div
            initial={{ opacity: 0, x: -20 }}
            animate={{ opacity: 1, x: 0 }}
            className="bg-white rounded-xl border border-gray-200 overflow-hidden"
          >
            <div className="p-6">
              <div className="flex items-center justify-between mb-4">
                <h2 className="text-lg font-semibold text-gray-900">{t('LiveTesting.transcription.title')}</h2>
                {isRecording && (
                  <span className="flex items-center text-sm text-violet-600">
                    <span className="relative flex h-3 w-3 mr-2">
                      <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-violet-400 opacity-75"></span>
                      <span className="relative inline-flex rounded-full h-3 w-3 bg-violet-500"></span>
                    </span>
                    {t('LiveTesting.transcription.recording_indicator')}
                  </span>
                )}
              </div>
              <div className="bg-gray-50 rounded-lg p-4 min-h-[200px] max-h-[400px] overflow-y-auto">
                <p className="text-gray-700 whitespace-pre-wrap">
                  {transcription || t('LiveTesting.transcription.placeholder')}
                </p>
              </div>
            </div>
          </motion.div>

          {/* Assistant Response Card */}
        <motion.div
          initial={{ opacity: 0, x: 20 }}
          animate={{ opacity: 1, x: 0 }}
          className="bg-white rounded-xl border border-gray-200 overflow-hidden"
        >
          <div className="p-6">
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-lg font-semibold text-gray-900">{t('LiveTesting.assistant_response.title')}</h2>
              {audioSourceRef.current && (
                <div className="flex items-center text-sm text-blue-600">
                  <Volume2 className="w-4 h-4 mr-1" />
                  {t('LiveTesting.assistant_response.audio_playing')}
                </div>
              )}
            </div>
            <div className="bg-gray-50 rounded-lg p-4 min-h-[200px] max-h-[400px] overflow-y-auto">
              <p className="text-gray-700 whitespace-pre-wrap">
                {response || t('LiveTesting.assistant_response.placeholder')}
              </p>
              
              {/* Cost Information */}
              {response && (
                <div className="mt-4 border-t border-gray-200 pt-4">
                  <h3 className="text-sm font-medium text-gray-600">API Usage Cost v1.2:</h3>
                  <p className="text-sm text-gray-700">Total Cost: ${totalCost.toFixed(4)}</p>
                  <ul className="list-disc list-inside text-sm text-gray-700">
                    <li>Text Input: ${costDetails.textInput.toFixed(4)}</li>
                    <li>Cached Text Input: ${costDetails.cachedTextInput.toFixed(4)}</li>
                    <li>Audio Input: ${costDetails.audioInput.toFixed(4)}</li>
                    <li>Cached Audio Input: ${costDetails.cachedAudioInput.toFixed(4)}</li>
                    <li>Text Output: ${costDetails.textOutput.toFixed(4)}</li>
                    <li>Audio Output: ${costDetails.audioOutput.toFixed(4)}</li>
                  </ul>
                </div>
              )}
            </div>
          </div>
        </motion.div>



          <KnowledgeBaseSection 
            onLoading={onLoading}
            onMessage={onMessage}
            onError={onError}
            onKbActivation={handleKbActivation}
          />
          
          <PromptSettingsSection
            onLoading={onLoading}
            onMessage={onMessage}
            onError={onError}
            onPromptActivation={handlePromptActivation}
          />
        </div>
      </Container>
    </div>
  );
};

export default LiveTesting;
